import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { LayoutProps, MenuItem } from './Layout.types';
import { useSessionStore } from '../../lib/Store/sessionStore.component';
import { SvgComponent } from '../../components/Svg/Svg.component';
import { useResponsive } from '@hooks';
import { Dialog } from '../../components/Dialog';

import InfoDevice from '../../components/InfoDevice/InfoDevice.component';

import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { stdCouleurBleu } from '@commonStyle';
import styles from './Layout.styles';

import Logo from '../../assets/svg/logo.svg';
import { Collectivite } from '../../modules/Collectivite';
import MenuMobile from './Menus/MenuMobile';
import MenuWeb from './Menus/MenuWeb';
import MenuProfil from './Menus/MenuProfil';
import SelectionFicheAgent from '../../modules/SelectionFicheAgent/SelectionFicheAgent.component';
import MenusStyles from './Menus/Menus.styles';
import { organigrammeAccessible } from '../../modules/Organigramme/Organigramme.utils';

export const Layout: FunctionComponent<LayoutProps> = ({ children, showMenu = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isResponsive = useResponsive();
  const { accessibleDatabases, session, clearSession, getCurrentInfos } = useSessionStore();
  const {currentLogin, currentEntite, currentDatabase} = getCurrentInfos()
  const [showCollectivitePopup, setShowCollectivitePopup] = useState(false);
  const [showFicheAgentPopup, setShowFicheAgentPopup] = useState(false);
  const [showMenuProfil, setShowMenuProfil] = useState(false);

  const menuItems = useMemo<MenuItem[]>(() => {
    const items = [
      { path: '/Accueil', label: 'Accueil', icon: 'home-outline', tabs: true, mobileOnly: true },
      { path: '/Actualite', label: 'Actualités', icon: 'email-newsletter', tabs: false },
      { path: '/DemandeAbsence', label: 'Demandes d\'absences', icon: 'briefcase-outline', tabs: true },
      { path: '/Calendrier', label: 'Calendrier', icon: 'calendar-outline', tabs: true },
      { path: '/FicheSalaire', label: 'Fiches de Paie', icon: 'currency-eur', tabs: true },
      { path: '/Droits', label: 'Droits', icon: 'ticket-confirmation', tabs: false },
      { path: '/Autres', label: 'Plus', icon: 'apps-box', tabs: true, mobileOnly: true},
    ]
    if(process.env.NODE_ENV === 'development'){
      items.push({ path: '/EspaceValideur', label: 'Espace valideur', icon: 'clipboard-check-multiple-outline', tabs: false})
      items.push({ path: '/GestionCET', label: 'CET', icon: 'piggy-bank-outline', tabs: false})
      if(organigrammeAccessible(currentEntite?.collectivitegr)){
        items.push({ path: '/Organigramme', label: 'Organigramme', icon: 'sitemap-outline', tabs: false})
      }
      items.push({ path: '/ConfrontationPlanning', label: 'Planning', icon: 'calendar-clock-outline', tabs: false})
      items.push({ path: '/DemandeHeuresSupplementaires', label: 'Heures supp.', icon: 'clock-plus-outline', tabs: false})
      items.push({ path: '/Carriere', label: 'Carrière', icon: 'stairs-up', tabs: false})
    }
    return items;
  }, [currentEntite?.collectivitegr]) ;

  // Vérifier si le menu doit être désactivé
  const isMenuDisabled = useMemo(() => !session?.base, [session?.base]);

  const handleLogout = useCallback(async () => {
    await clearSession();
    navigate('/');
  }, [clearSession, navigate]);

  const toggleMenuProfile = useCallback(() => setShowMenuProfil(prev => !prev), [])
  const toggleCollectivitePopup = useCallback(() => setShowCollectivitePopup(prev => !prev), [])
  const toggleFicheAgentPopup = useCallback(() => setShowFicheAgentPopup(prev => !prev), [])

  const handleMenuItemPress = useCallback((path: string) => {
    if (session?.base?.tenant || path === '/Collectivite') {
      navigate(path);
    }
  }, [navigate, session?.base?.tenant])

  const navigateToProfile = useCallback(() => handleMenuItemPress('/Profil'), [handleMenuItemPress])

  const insets = useSafeAreaInsets();

  if (!showMenu) {
    return (
      <View style={styles.container}>
        {children}
      </View>
    );
  }

  const styleMobileOnly = isResponsive ? { paddingBottom: 60 + insets.bottom } : { };

  return (
    <>
      <View style={[styles.container, styleMobileOnly]}>
        {/* MenuMobile pour écrans non-responsives */}
        {isResponsive
          ? <MenuMobile menuItems={menuItems} />
          : (
            <>
              {/* Header - affiché seulement sur écrans responsives (pas de tabs) */}
              <View style={styles.header}>
                <View style={{ width: 240, alignItems: 'center', gap: 15 }}>
                  <SvgComponent svgFile={Logo} width={80} height={80} />
                   <InfoDevice />
                </View>
                <View>
                  <Text style={styles.collectiviteTitle}>
                    {currentDatabase?.libelleDomaine || currentEntite?.libelle}
                  </Text>
                </View>
                <View style={styles.profileButtonContainer}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 15}}>
                    <Text style={{...MenusStyles.menuItemText, fontSize: 18}}>Bonjour {currentLogin?.nomClassement}</Text>
                    <Pressable style={{flexDirection: 'row'}} onPress={toggleMenuProfile} testID='BT_SHOW_MENU_PROFIL'>
                      <MaterialCommunityIcons name="account-circle" color={stdCouleurBleu} size={32} />
                      <MaterialCommunityIcons name="chevron-down" color={stdCouleurBleu} size={32} />
                    </Pressable>
                  </View>
                  {showMenuProfil && (
                    <MenuProfil
                      onProfilClick={navigateToProfile}
                      onFicheAgentClick={currentDatabase && currentDatabase?.fichesAgent.length > 1 ? toggleFicheAgentPopup : undefined}
                      onCollectiviteClick={accessibleDatabases.length > 1 ? toggleCollectivitePopup : undefined}
                      onLogoutClick={handleLogout}
                    />
                  )}
                </View>
              </View>
            </>
          )
        }

        {/* Popup de sélection de collectivité */}
        <Dialog
          visible={showCollectivitePopup}
          title="Sélectionnez une collectivité"
          width="40%"
          onClose={toggleCollectivitePopup}
        >
          <Collectivite withoutTitle/>
        </Dialog>

        {/* Popup de sélection de fiche agent */}
        <Dialog
          visible={showFicheAgentPopup}
          title="Sélectionnez un profil"
          width="40%"
          onClose={toggleFicheAgentPopup}
        >
          <SelectionFicheAgent withoutTitle/>
        </Dialog>

       <View style={styles.contentContainer}>
          {/* MenuWeb pour écrans responsives */}
          {!isResponsive && (
            <MenuWeb
              isMenuDisabled={isMenuDisabled}
              location={location}
              handleMenuItemPress={handleMenuItemPress}
              menuItems={menuItems}
            />
          )}

          {/* Main Content */}
          <View style={styles.mainContent}>
            {children}
          </View>
        </View>
      </View>

    </>
)
};
